import React, { useEffect, useMemo } from 'react';

import classnames from 'classnames';
import { Link } from 'react-router-dom';

import { can } from '../can';
import { routes } from '../routes';
import api from '../utils/api';
import { formatDateTime } from '../utils';
import { useQuery } from '../utils/query';

import { roles, roleLabels, userStatusLabels } from '../constants';

import { Loading } from './PageStatus';

import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Select, { indexToOptions, objectToOptions } from '../components/common/Select';
import Input from '../components/common/Input';

import Toggle from '../components/common/Toggle';
import { List, Header, Body, Row, Cell } from '../components/common/List';
import Pagination from '../components/common/Pagination';

import css from './Users.css';

const Users = (props) => {
  const query = useQuery(props.location, props.history);
  const { user } = api.auth();
  const { index, actions } = api.users.index(query.obj);
  const { item: client, actions: clientActions } = api.clients.item(null, null, { lazy: true });
  const { actions: clientsActions } = api.clients.index(null, { lazy: true });
  const selectedClientId = user.clientId || query.get('clientId');
  const clientPreferences = client.data ? client.data.get('preferences').toJS() : {};
  const nationalId = !!selectedClientId && clientPreferences.enableUsersNationalId;
  const clientMedicalEnabled = !!selectedClientId && clientPreferences.medical === true;
  const roleLabelOptions = { ...roleLabels.obj };
  delete roleLabelOptions[roles('admin')];
  if (clientMedicalEnabled) {
    delete roleLabelOptions[roles('resident')];
    roleLabelOptions.noAccess = 'Pacientes';
  }
  const roleOptions = [
    { value: '', label: 'Todos' },
    { value: '!noAccess', label: 'Con acceso al sistema' },
    ...objectToOptions(roleLabelOptions),
  ];

  const getClients = async (search) => {
    console.log(search);
    return indexToOptions(await clientsActions.get({ search, perPage: 50 }));
  };

  const isAdmin = user.role == roles('admin');

  useMemo(() => {
    if (selectedClientId) clientActions.get(null, null, selectedClientId);
  }, [selectedClientId]);

  return (
    <main className={css.root}>
      <PageHeader title="Usuarios">
        {can(user, 'create', 'users') && (
          <Button noMargin className={css.createUserButton} to={routes('newUser')}>
            Nuevo
          </Button>
        )}
      </PageHeader>
      <div className={css.toolBar}>
        <Input
          className={css.select}
          label="Buscar por nombre o email:"
          name="search"
          onChange={query.set}
          value={query.get('search')}
          placeholder="Nombre o Email"
        />
        {nationalId && (
          <Input
            className={css.select}
            label="Buscar por id nacional:"
            name="searchNationalId"
            onChange={query.set}
            value={query.get('searchNationalId')}
            placeholder="Rut"
          />
        )}
        {can(user, 'index', 'clients') && (
          <Select
            className={css.select}
            label="Cliente"
            name="clientId"
            value={query.get('clientId')}
            onChange={query.set}
            placeholder="Todos"
            loadOptions={getClients}
          />
        )}
        <Select
          className={classnames(css.select, css.roleSelect)}
          label="Rol"
          name="role"
          value={query.get('role')}
          onChange={query.set}
          placeholder="Todos"
          options={roleOptions}
        />

      </div>
      {index.loaded ? (
        <List>
          <Header>
            <Cell head>Id</Cell>
            <Cell head>Rol</Cell>
            <Cell head>Nombre</Cell>
            {(!isAdmin && nationalId) && <Cell head>Cédula</Cell>}
            <Cell head>Email</Cell>
            <Cell head>Fecha de creación</Cell>
            {isAdmin && <Cell head>Cliente</Cell>}
          </Header>
          <Body>
            {index.data.toJS().map((u, i) => {
              const to = routes('user', { id: u.id });
              const roleRowClass =
                u.role === roles('noAccess')
                  ? css.noAccessRow
                  : u.role === roles('client')
                    ? css.clientRow
                    : [roles('manager'), roles('courier'), roles('viewer'), roles('resident')].includes(u.role)
                      ? css.standardRoleRow
                      : null;
              return (
                <Row
                  className={classnames(roleRowClass, { [css.readOnly]: !can(user, 'show', 'users', u) })}
                  key={`user-row-${i}`}>
                  <Cell>
                    <Link to={to}>{u.id}</Link>
                  </Cell>
                  <Cell>
                    <Link className={css.roleLabel} to={to}>
                      {clientMedicalEnabled && u.role === roles('noAccess') ? 'Paciente' : roleLabels(u.role)}
                    </Link>
                    {u.status !== 'active' && (
                      <span className={css.inactiveBadge} title="Bloqueado para acceder al sistema">
                        {userStatusLabels(u.status)}
                      </span>
                    )}
                  </Cell>
                  <Cell>
                    <Link to={to}>{u.name}</Link>
                  </Cell>
                  {(!isAdmin && nationalId) && <Cell>{u.nationalId}</Cell>}
                  <Cell>
                    <Link to={to}>{u.email}</Link>
                  </Cell>
                  <Cell>
                    <Link to={to}>{formatDateTime(u.createdAt) || '...'}</Link>
                  </Cell>
                  {isAdmin && (
                    <Cell>
                      {u.clientId ? (
                        <Link to={routes('client', { id: u.clientId })}>{u.clientName || u.clientId}</Link>
                      ) : (
                        'Admin'
                      )}
                    </Cell>
                  )}
                </Row>
              );
            })}
          </Body>
        </List>
      ) : (
        <Loading compact />
      )}
      {index.pageInfo && <Pagination {...index.pageInfo.toJS()} onChange={query.setPage} />}
    </main>
  );
};

export default Users;
